// Timeline / arranger: a synced track-headers column (DOM) + a canvas lane area
// that draws the ruler, clips with cached waveforms, the playhead, and loop region.
import { Store } from "../state/store";
import { AudioEngine } from "../audio/engine";
import { InputDevice, LivePeaks } from "../audio/recorder";
type LivePeaksAt = LivePeaks & { startSec: number };
import { el, secPerBeat, fmtBars, setStyle } from "../util";
import { Track, newTrack, windowedNotes } from "../state/model";
import { BASE_TRACK_HEIGHT, TAKE_ROW_HEIGHT } from "../state/model";
import { openContextMenu } from "./modal";
import { openInstrumentPanel } from "./instrument-panel";

const RULER_H = 28;
const clampH = (h: number) => Math.max(56, Math.min(400, h || 0)) || 120;
const clampW = (w: number) => Math.max(150, Math.min(520, w || 0)) || 220;

/** Waveform overview, computed ONCE per audio buffer: min/max over every
 *  PEAK_BIN samples (all channels). Any zoom level is drawn by aggregating these
 *  bins, so zooming and editing never rescan the raw audio. Keyed by the buffer
 *  object itself — assets are immutable (edits create new assets). */
const PEAK_BIN = 64;
interface PeakMip { binsPerSec: number; min: Float32Array; max: Float32Array; }
const peakCache = new WeakMap<AudioBuffer, PeakMip>();
function peakMip(buf: AudioBuffer): PeakMip {
  let m = peakCache.get(buf);
  if (m) return m;
  const n = Math.ceil(buf.length / PEAK_BIN);
  const min = new Float32Array(n).fill(1), max = new Float32Array(n).fill(-1);
  for (let c = 0; c < buf.numberOfChannels; c++) {
    const d = buf.getChannelData(c);
    for (let b = 0, i = 0; b < n; b++) {
      let mn = min[b], mx = max[b];
      const e = Math.min(d.length, i + PEAK_BIN);
      for (; i < e; i++) { const v = d[i]; if (v < mn) mn = v; if (v > mx) mx = v; }
      min[b] = mn; max[b] = mx;
    }
  }
  m = { binsPerSec: buf.sampleRate / PEAK_BIN, min, max };
  peakCache.set(buf, m);
  return m;
}

export class Timeline {
  el = el("div", { class: "arranger" });
  private headsEl = el("div", { class: "track-heads" });
  private wrapEl = el("div", { class: "timeline-wrap" });
  private canvas = document.createElement("canvas");
  private c2d = this.canvas.getContext("2d")!;
  private cache = document.createElement("canvas");
  private cc2d = this.cache.getContext("2d")!;
  private dirty = true;

  pps = 80;
  scrollX = 0; // px
  scrollY = 0; // px
  // Global track row height (vertical zoom), persisted; lanes + headers use it.
  trackHeight = clampH(Number(localStorage.getItem("webdaw.trackHeight")) || BASE_TRACK_HEIGHT);
  private headsResizer = el("div", { class: "heads-resizer", attrs: { title: "Drag to resize the track header column" } });
  private devices: InputDevice[] = [];

  private drag: { clipId: string; mode: "move" | "left" | "right" | "gain"; startSec: number; grabX: number; startGain: number; grabY: number; laneH: number; starts: { id: string; startSec: number }[]; undone: boolean } | null = null;
  private marquee: { x0: number; y0: number; x1: number; y1: number } | null = null;
  private pan: { startX: number; startScrollX: number; startY: number; startTop: number; moved: boolean; laneId: string | null; seekX: number } | null = null;
  private dragY = 0;
  private scrubbing = false;
  private loopDrag: { s0: number; s1: number } | null = null; // Shift-drag on the ruler
  private lastFrame = "";
  private lastFollowPos = -1;

  /** Keep the playhead on screen: page-flip while playing, scroll-into-view on seek.
   *  Skips while the user is actively scrubbing/panning/dragging. */
  followPlayhead(pos: number, playing: boolean) {
    if (this.scrubbing || this.pan || this.drag || this.hdrag) return;
    // Scroll while playing (page-flip) or whenever the position jumps (seek, or
    // stop → 0). Always track lastFollowPos so a return to 0 is detected even if
    // playback started from 0.
    if (playing || Math.abs(pos - this.lastFollowPos) > 1e-4) this.scrollToTime(pos);
    this.lastFollowPos = pos;
  }
  private hbar = el("div", { class: "h-scroll" });
  private hthumb = el("div", { class: "h-thumb" });
  private hdrag: { startX: number; startScroll: number } | null = null;
  private readonly EDGE = 7; // px hit zone for trim handles
  snap = true; // snap clip moves/edits to the beat grid (Alt inverts)

  /** Snap a time to the nearest beat when snapping is active (Alt inverts). */
  private snapTime(sec: number, alt: boolean): number {
    if (this.snap === alt) return sec; // snap XOR alt
    const spb = secPerBeat(this.store.project.tempo);
    return Math.round(sec / spb) * spb;
  }
  /** Y within a clip for its gain line (gain 2→top, 1→middle, 0→bottom). */
  private gainLineY(top: number, ch: number, gain: number): number {
    return top + (1 - Math.max(0, Math.min(1, gain / 2))) * ch;
  }

  constructor(
    private store: Store,
    private engine: AudioEngine,
    private cb: { onChange?: () => void; editClip?: (clipId: string) => void; dropAudio?: (files: File[], trackId: string | null, startSec: number) => void } = {},
  ) {
    this.hbar.append(this.hthumb);
    this.wrapEl.append(this.canvas, this.hbar);
    this.el.append(this.headsEl, this.headsResizer, this.wrapEl);
    const hw = Number(localStorage.getItem("webdaw.headsWidth"));
    if (hw) this.headsEl.style.width = clampW(hw) + "px";
    this.bind();
    this.bindHScroll();
    this.bindHeadsResize();
    this.bindFileDrop();
    store.on("change", ({ scope }) => {
      if (scope === "tracks" || scope === "project" || scope === "assets") {
        this.invalidate();
        // Rebuilding the header DOM is costly and destroys focused inputs, so only
        // do it when something the headers display actually changed (not on every
        // clip drag/trim mouse-move).
        if (this.headsSignature() !== this.headsSig) this.rebuildHeads();
      }
      if (scope === "transport") this.invalidate(); // loop region / loop on-off
      if (scope === "selection") this.markSelection();
    });
    store.on("selection", () => { this.markSelection(); this.invalidate(); });
  }

  setZoom(pps: number) {
    this.pps = Math.max(8, Math.min(400, pps));
    this.invalidate();
  }

  /** Global track row height (vertical zoom). Headers + canvas lanes follow it. */
  setTrackHeight(px: number) {
    this.trackHeight = clampH(px);
    localStorage.setItem("webdaw.trackHeight", String(this.trackHeight));
    this.rebuildHeads();
    this.invalidate();
  }

  /** Drop audio files from the OS onto the timeline → import at that track/time. */
  private bindFileDrop() {
    const wrap = this.wrapEl;
    const isFileDrag = (e: DragEvent) => !!e.dataTransfer && Array.from(e.dataTransfer.types).includes("Files");
    wrap.addEventListener("dragover", (e) => {
      if (!isFileDrag(e)) return;
      e.preventDefault();
      e.dataTransfer!.dropEffect = "copy";
      wrap.classList.add("drag-over");
    });
    wrap.addEventListener("dragleave", (e) => { if (e.target === wrap) wrap.classList.remove("drag-over"); });
    wrap.addEventListener("drop", (e) => {
      wrap.classList.remove("drag-over");
      if (!e.dataTransfer?.files?.length) return;
      const audio = Array.from(e.dataTransfer.files).filter((f) => f.type.startsWith("audio/") || /\.(wav|mp3|m4a|aac|ogg|flac|opus|webm|aif|aiff)$/i.test(f.name));
      if (!audio.length) return;
      e.preventDefault();
      const rect = wrap.getBoundingClientRect();
      const x = e.clientX - rect.left, y = e.clientY - rect.top;
      const lane = this.laneTops().find((l) => y >= l.top && y < l.top + l.h);
      const sec = Math.max(0, this.snapTime(this.xToSec(x), false));
      this.cb.dropAudio?.(audio, lane ? lane.id : null, sec);
    });
  }

  /** Drag the divider on the right edge of the track-header column to resize it. */
  private bindHeadsResize() {
    this.headsResizer.addEventListener("mousedown", (e) => {
      e.preventDefault();
      const startX = e.clientX, startW = this.headsEl.offsetWidth;
      document.body.style.cursor = "col-resize";
      const move = (ev: MouseEvent) => {
        const w = clampW(startW + (ev.clientX - startX));
        this.headsEl.style.width = w + "px";
        localStorage.setItem("webdaw.headsWidth", String(w));
        this.layout();
      };
      const up = () => { document.body.style.cursor = ""; window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up); };
      window.addEventListener("mousemove", move);
      window.addEventListener("mouseup", up);
    });
  }

  mount() {
    requestAnimationFrame(() => { this.layout(); this.rebuildHeads(); });
    new ResizeObserver(() => this.layout()).observe(this.wrapEl);
  }
  setDevices(d: InputDevice[]) { this.devices = d; this.rebuildHeads(); }
  invalidate() { this.dirty = true; }

  private layout() {
    const dpr = window.devicePixelRatio || 1;
    const w = this.wrapEl.clientWidth, h = this.wrapEl.clientHeight;
    for (const cv of [this.canvas, this.cache]) {
      cv.width = Math.max(1, Math.floor(w * dpr));
      cv.height = Math.max(1, Math.floor(h * dpr));
      cv.style.width = w + "px"; cv.style.height = h + "px";
    }
    this.c2d.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.cc2d.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.invalidate();
  }

  // ---- track headers ---------------------------------------------------
  private headsSig = "";
  /** Everything the header DOM shows — rebuilt only when this changes. */
  private headsSignature(): string {
    return JSON.stringify([this.trackHeight, this.devices.map((d) => d.deviceId + d.label), this.store.project.tracks.map((t) => [
      t.id, t.name, t.kind, t.color, t.mute, t.solo, t.armed, t.monitor, t.inputDeviceId, t.inputChannel ?? "1", t.inputGain ?? 0,
      t.instrument?.type, (t.instrument as any)?.preset, t.compExpanded, this.drawerRows(t),
      new Set(t.clips.filter((c) => c.takeGroupId).map((c) => c.takeGroupId)).size,
    ])]);
  }

  rebuildHeads() {
    this.headsSig = this.headsSignature();
    this.headsEl.innerHTML = "";
    const pad = el("div", { class: "ruler-pad", text: "Tracks" });
    this.headsEl.append(pad);
    // Native vertical scroll for the track list; the canvas lane area follows it.
    const inner = el("div", { class: "heads-scroll" });
    inner.addEventListener("scroll", () => { this.scrollY = inner.scrollTop; this.invalidate(); });
    this.headsEl.append(inner);
    for (const t of this.store.project.tracks) {
      // Progressive density: drop the least-critical controls as the row shrinks
      // so the header never overflows (ingain → device → meter, in that order).
      const dh = this.displayHeight(t);
      // thresholds = each tier's intrinsic content height (+margin): full ~117,
      // th-m ~94 (no ingain), th-s ~65 (no ingain/device), th-xs name+buttons.
      const tier = dh < 67 ? " th-xs" : dh < 96 ? " th-s" : dh < 120 ? " th-m" : "";
      const head = el("div", { class: "track-head" + (t.id === this.store.selectedTrackId ? " sel" : "") + tier });
      head.style.height = dh + "px";
      head.addEventListener("mousedown", (e) => {
        if ((e.target as HTMLElement).tagName === "INPUT" || (e.target as HTMLElement).tagName === "SELECT" || (e.target as HTMLElement).tagName === "BUTTON") return;
        this.store.select(t.id, null);
      });
      const sw = el("div", { class: "swatch" }); sw.style.background = t.color;
      const name = el("input", { class: "name", attrs: { value: t.name } }) as HTMLInputElement;
      name.value = t.name;
      name.addEventListener("change", () => this.store.updateTrack(t.id, { name: name.value }, "tracks"));

      const mkMini = (label: string, cls: string, on: boolean, fn: () => void, title?: string) => {
        const b = el("button", { class: `mini ${cls}` + (on ? ` on ${cls}` : ""), text: label });
        if (title) b.title = title;
        b.addEventListener("click", (e) => { e.stopPropagation(); fn(); });
        return b;
      };
      const row1 = el("div", { class: "row" }, [name]);
      const row2 = el("div", { class: "row" }, [
        mkMini("M", "m", t.mute, () => this.store.updateTrack(t.id, { mute: !t.mute }, "tracks"), "Mute this track"),
        mkMini("S", "s", t.solo, () => this.store.updateTrack(t.id, { solo: !t.solo }, "tracks"), "Solo this track (silence others)"),
        mkMini("R", "r", t.armed, () => this.store.updateTrack(t.id, { armed: !t.armed }, "tracks"), "Record-arm this track for recording"),
        mkMini("🎧", "mon", t.monitor, () => this.store.updateTrack(t.id, { monitor: !t.monitor }, "tracks"), "Input monitor — hear the live mic input through this track while armed (use headphones to avoid feedback)"),
      ]);
      if (t.kind === "instrument") {
        const label = t.instrument?.type === "gm" ? "GM instrument" : (t.instrument?.type === "synth" ? t.instrument.preset : "synth");
        const ib = el("button", { class: "mini exp", text: "🎹", attrs: { title: `Instrument: ${label} — click to change/edit sound` } });
        ib.addEventListener("click", (e) => { e.stopPropagation(); openInstrumentPanel(this.store, this.engine, t.id); });
        row2.append(ib);
      }
      const takeCount = new Set(t.clips.filter((c) => c.takeGroupId).map((c) => c.takeGroupId)).size;
      if (takeCount > 0) {
        const exp = el("button", { class: "mini exp" + (t.compExpanded ? " on" : ""), text: `⤢ Takes`, attrs: { title: "Show/hide alternate takes (comp lane)" } });
        exp.addEventListener("click", (e) => { e.stopPropagation(); this.store.toggleCompExpand(t.id); });
        row2.append(exp);
      }

      const dev = el("select") as HTMLSelectElement;
      dev.append(el("option", { text: "Default input", attrs: { value: "" } }));
      for (const d of this.devices) {
        const o = el("option", { text: d.label, attrs: { value: d.deviceId } }) as HTMLOptionElement;
        if (t.inputDeviceId === d.deviceId) o.selected = true;
        dev.append(o);
      }
      dev.addEventListener("change", () => this.store.updateTrack(t.id, { inputDeviceId: dev.value || null }, "tracks"));
      // which interface input(s) to record — mono In 1 by default (DAW convention),
      // so an unused 2nd input's preamp hiss isn't captured into the take
      const chan = el("select", { class: "inchan", attrs: { title: "Input channel to record: mono In 1 (default for a mic), mono In 2, or Stereo (both inputs)" } }) as HTMLSelectElement;
      for (const [v, label] of [["1", "In 1"], ["2", "In 2"], ["stereo", "Stereo"]] as const) {
        const o = el("option", { text: label, attrs: { value: v } }) as HTMLOptionElement;
        if ((t.inputChannel ?? "1") === v) o.selected = true;
        chan.append(o);
      }
      chan.addEventListener("change", () => this.store.updateTrack(t.id, { inputChannel: chan.value as any }, "tracks"));
      const devRow = el("div", { class: "row devrow" }, [dev, chan]);

      // input gain (dB) — applied to live input (monitor + recording)
      const ig = t.inputGain ?? 0;
      const inSlider = el("input", { attrs: { type: "range", min: "-24", max: "24", step: "0.5", value: String(ig), title: "Input gain (dB) — boosts the recording level for a quiet mic" } }) as HTMLInputElement;
      const inOut = el("span", { text: `${ig > 0 ? "+" : ""}${ig} dB` });
      // scope "mixer" so the timeline doesn't rebuild headers mid-drag (which would
      // destroy this slider) — main syncs the live recorder gain on "mixer" too.
      inSlider.addEventListener("input", () => { const v = Number(inSlider.value); inOut.textContent = `${v > 0 ? "+" : ""}${v} dB`; this.store.updateTrack(t.id, { inputGain: v }, "mixer"); });
      const inRow = el("div", { class: "row ingain" }, [el("span", { class: "hint", text: "In" }), inSlider, inOut]);

      const fill = el("i");
      const peakMark = el("b", { class: "peak" });
      const meter = el("div", { class: "headmeter" }, [fill, peakMark]);
      (head as any)._meter = fill;
      (head as any)._peakMark = peakMark;
      (head as any)._meterWrap = meter;

      head.append(sw, row1, row2, devRow, inRow, meter);
      (head as any)._trackId = t.id;
      inner.append(head);
    }
    this.headsInner = inner;
    inner.scrollTop = this.scrollY; // restore scroll position after rebuild
  }
  private headsInner: HTMLElement | null = null;

  private markSelection() {
    this.headsEl.querySelectorAll(".track-head").forEach((h) => {
      h.classList.toggle("sel", (h as any)._trackId === this.store.selectedTrackId);
    });
  }

  private headPeak = new Map<string, number>();
  private headClip = new Map<string, number>();
  updateMeters(levels: Record<string, number>) {
    if (!this.headsInner) return;
    const now = performance.now();
    for (const head of Array.from(this.headsInner.children) as any[]) {
      const id = head._trackId;
      const lvl = levels[id] ?? 0;
      if (head._meter) setStyle(head._meter, "width", lvl * 100);
      const held = Math.max(lvl, (this.headPeak.get(id) ?? 0) * 0.985);
      this.headPeak.set(id, held);
      if (head._peakMark) setStyle(head._peakMark, "left", held < 0.001 ? 0 : held * 100);
      if (lvl >= 0.99) this.headClip.set(id, now + 1200);
      const clipping = now < (this.headClip.get(id) ?? 0);
      if (head._meterWrap && head._meterWrap.classList.contains("clip") !== clipping) head._meterWrap.classList.toggle("clip", clipping);
    }
  }

  // ---- drawing ---------------------------------------------------------
  private secToX(sec: number) { return sec * this.pps - this.scrollX; }
  private xToSec(x: number) { return (x + this.scrollX) / this.pps; }

  /** Number of take-alternate rows to show in a track's drawer when expanded. */
  private drawerRows(track: Track): number {
    if (!track.compExpanded) return 0;
    const counts = new Map<string, number>();
    for (const c of track.clips) if (c.takeGroupId) counts.set(c.takeGroupId, (counts.get(c.takeGroupId) ?? 0) + 1);
    let maxAlt = 0;
    for (const n of counts.values()) maxAlt = Math.max(maxAlt, n - 1);
    return Math.max(track.compExpanded && counts.size ? 1 : 0, maxAlt);
  }
  displayHeight(track: Track): number {
    return this.trackHeight + this.drawerRows(track) * TAKE_ROW_HEIGHT;
  }

  private laneTops(): { id: string; top: number; h: number; baseH: number; rows: number }[] {
    let y = RULER_H - this.scrollY;
    return this.store.project.tracks.map((t) => {
      const rows = this.drawerRows(t);
      const h = this.trackHeight + rows * TAKE_ROW_HEIGHT;
      const r = { id: t.id, top: y, h, baseH: this.trackHeight, rows };
      y += h; return r;
    });
  }

  /** Inactive take members of a group, in stable order, mapped to drawer rows. */
  private takeRow(track: Track, clip: { id: string; takeGroupId?: string }): number {
    if (!clip.takeGroupId) return 0;
    const alts = track.clips.filter((c) => c.takeGroupId === clip.takeGroupId && c.takeActive === false);
    return alts.findIndex((c) => c.id === clip.id);
  }

  private rebuildCache() {
    const w = this.wrapEl.clientWidth, h = this.wrapEl.clientHeight;
    const g = this.cc2d;
    g.clearRect(0, 0, w, h);
    g.fillStyle = "#0b0d12"; g.fillRect(0, 0, w, h);

    const p = this.store.project;
    const spb = secPerBeat(p.tempo);
    const barSec = spb * p.beatsPerBar;

    // lane backgrounds + grid
    const lanes = this.laneTops();
    for (const lane of lanes) {
      g.fillStyle = "#0e1118"; g.fillRect(0, lane.top, w, lane.h - 1);
    }
    // beat/bar grid
    const startSec = this.xToSec(0), endSec = this.xToSec(w);
    for (let b = Math.floor(startSec / spb); b * spb < endSec; b++) {
      const x = this.secToX(b * spb);
      const isBar = b % p.beatsPerBar === 0;
      g.strokeStyle = isBar ? "#232a38" : "#161b25";
      g.beginPath(); g.moveTo(x + 0.5, RULER_H); g.lineTo(x + 0.5, h); g.stroke();
    }

    // loop: shade an explicit region (or the one being dragged) across the lanes
    const loopR = p.loopEnabled ? this.store.loopRange() : null;
    const dragR = this.loopDrag ? { start: Math.min(this.loopDrag.s0, this.loopDrag.s1), end: Math.max(this.loopDrag.s0, this.loopDrag.s1) } : null;
    const shadeR = dragR ?? (loopR && p.loopEnd > p.loopStart ? loopR : null);
    if (shadeR) {
      g.fillStyle = "rgba(91,140,255,0.10)";
      g.fillRect(this.secToX(shadeR.start), RULER_H, (shadeR.end - shadeR.start) * this.pps, h - RULER_H);
    }

    // clips
    for (const lane of lanes) {
      const track = this.store.track(lane.id)!;
      // active clips in the main band
      for (const clip of track.clips) {
        if (clip.takeActive === false) continue;
        const x = this.secToX(clip.start), cw = clip.duration * this.pps;
        if (x + cw < 0 || x > w) continue;
        const selected = this.store.isClipSelected(clip.id);
        this.drawClip(g, x, lane.top + 2, cw, lane.baseH - 6, clip, track, selected, false, selected, w);
      }
      // take drawer (alternates)
      if (lane.rows > 0) {
        g.fillStyle = "#090b10";
        g.fillRect(0, lane.top + lane.baseH, w, lane.h - lane.baseH);
        for (const clip of track.clips) {
          if (clip.takeActive !== false || !clip.takeGroupId) continue;
          const row = this.takeRow(track, clip);
          const y = lane.top + lane.baseH + row * TAKE_ROW_HEIGHT;
          const x = this.secToX(clip.start), cw = clip.duration * this.pps;
          if (x + cw < 0 || x > w) continue;
          this.drawClip(g, x, y + 2, cw, TAKE_ROW_HEIGHT - 4, clip, track, false, true, false, w);
        }
      }
    }

    // ruler
    g.fillStyle = "#11151d"; g.fillRect(0, 0, w, RULER_H);
    g.strokeStyle = "#2a3140"; g.beginPath(); g.moveTo(0, RULER_H + .5); g.lineTo(w, RULER_H + .5); g.stroke();
    g.fillStyle = "#8a95a6"; g.font = "10px Segoe UI";
    for (let bar = Math.floor(startSec / barSec); bar * barSec < endSec; bar++) {
      const x = this.secToX(bar * barSec);
      g.strokeStyle = "#2a3140"; g.beginPath(); g.moveTo(x + .5, 14); g.lineTo(x + .5, RULER_H); g.stroke();
      g.fillText(String(bar + 1), x + 4, 12);
    }
    // active loop range on the ruler (the region, or the whole song when none is set)
    const barR = dragR ?? loopR;
    if (barR) {
      const x0 = this.secToX(barR.start), x1 = this.secToX(barR.end);
      g.fillStyle = dragR ? "rgba(91,140,255,0.55)" : "#5b8cff";
      g.fillRect(x0, RULER_H - 5, Math.max(2, x1 - x0), 4);
    }
    this.dirty = false;
  }

  private drawClip(
    g: CanvasRenderingContext2D, x: number, top: number, cw: number, ch: number,
    clip: any, track: Track, selected: boolean, dim: boolean, handles: boolean, w: number,
  ) {
    g.globalAlpha = dim ? 0.55 : 1;
    g.fillStyle = track.color + (dim ? "22" : "33");
    g.fillRect(x, top, cw, ch);
    g.strokeStyle = selected ? "#ffffff" : track.color;
    g.lineWidth = selected ? 2 : 1;
    g.strokeRect(x + 0.5, top + 0.5, cw - 1, ch - 1);
    if (clip.notes) {
      this.drawMidiNotes(g, x, top, cw, ch, clip, track, w);
    } else {
      const buf = this.store.buffers.get(clip.assetId);
      if (buf) {
        const mip = peakMip(buf);
        g.fillStyle = track.color;
        // scale the drawn waveform by the clip's gain so boosts/cuts are visible
        // (clamped to the clip box below, so hot gain squashes against the edges)
        const mid = top + ch / 2, amp = (ch / 2 - 3) * (clip.gain ?? 1);
        const yLo = top + 1, yHi = top + ch - 1; // clamp drawing to the clip box
        const nb = mip.min.length, bps = mip.binsPerSec;
        // only the on-screen columns of the clip
        const i0 = Math.max(0, Math.floor(-x)), i1 = Math.min(Math.floor(cw), Math.ceil(w - x));
        for (let i = i0; i < i1; i++) {
          const b0 = Math.floor((clip.offset + i / this.pps) * bps);
          if (b0 >= nb) break;
          const b1 = Math.min(nb, Math.max(b0 + 1, Math.floor((clip.offset + (i + 1) / this.pps) * bps)));
          let mn = 1, mx = -1;
          for (let b = b0; b < b1; b++) { if (mip.min[b] < mn) mn = mip.min[b]; if (mip.max[b] > mx) mx = mip.max[b]; }
          if (mx < mn) continue;
          const yt = Math.max(yLo, mid - mx * amp);
          const yb = Math.min(yHi, mid - mn * amp);
          if (yb > yt) g.fillRect(x + i, yt, 1, Math.max(1, yb - yt));
        }
      }
    }
    g.fillStyle = "#dfe6f0"; g.font = "11px Segoe UI";
    g.save(); g.beginPath(); g.rect(x, top, cw, 14); g.clip();
    g.fillText(clip.name, x + 5, top + 11); g.restore();
    if (handles) {
      g.fillStyle = "#ffffff";
      g.fillRect(x, top, 3, ch);
      g.fillRect(x + cw - 3, top, 3, ch);
      // gain line (drag vertically to change clip gain)
      const gy = this.gainLineY(top, ch, clip.gain ?? 1);
      g.strokeStyle = "rgba(255,255,255,0.7)"; g.lineWidth = 1.5;
      g.setLineDash([4, 3]);
      g.beginPath(); g.moveTo(x + 3, gy); g.lineTo(x + cw - 3, gy); g.stroke();
      g.setLineDash([]);
    }
    g.globalAlpha = 1;
  }

  /** Draw a MIDI clip's notes as a mini piano-roll inside the clip box. */
  private drawMidiNotes(g: CanvasRenderingContext2D, x: number, top: number, cw: number, ch: number, clip: any, track: Track, w: number) {
    // window-relative notes so a trimmed clip shows exactly what it plays
    const notes = windowedNotes(clip);
    if (!notes.length) return;
    let lo = Infinity, hi = -Infinity;
    for (const n of notes) { if (n.midi < lo) lo = n.midi; if (n.midi > hi) hi = n.midi; }
    if (hi - lo < 11) { const c = (lo + hi) / 2; lo = Math.floor(c - 6); hi = Math.ceil(c + 6); } // min ~1 octave span
    const padY = 3, areaH = ch - 2 * padY;
    const rowH = Math.max(2, Math.min(7, areaH / (hi - lo + 1)));
    g.fillStyle = track.color;
    for (const n of notes) {
      const nx = x + n.start * this.pps;
      const nw = Math.max(2, n.dur * this.pps);
      if (nx > w || nx + nw < 0) continue;
      const ny = top + padY + (1 - (n.midi - lo) / (hi - lo)) * (areaH - rowH);
      g.globalAlpha = 0.35 + 0.6 * Math.max(0, Math.min(1, n.vel));
      g.fillRect(Math.max(x + 1, nx), ny, Math.min(nw, x + cw - 1 - nx), rowH);
    }
    g.globalAlpha = 1;
  }

  private contentWidth(): number {
    return Math.max(this.wrapEl.clientWidth, this.store.contentLength() * this.pps + 200);
  }

  private bindHScroll() {
    this.hthumb.addEventListener("mousedown", (e) => {
      e.stopPropagation(); e.preventDefault();
      this.hdrag = { startX: e.clientX, startScroll: this.scrollX };
    });
    this.hbar.addEventListener("mousedown", (e) => {
      if (e.target !== this.hbar) return; // clicked the track, not the thumb → page
      const w = this.wrapEl.clientWidth, content = this.contentWidth();
      const frac = e.offsetX / w;
      this.scrollX = Math.max(0, Math.min(content - w, frac * content - w / 2));
      this.invalidate();
    });
    window.addEventListener("mousemove", (e) => {
      if (!this.hdrag) return;
      const w = this.wrapEl.clientWidth, content = this.contentWidth();
      const thumbW = Math.max(30, (w / content) * w);
      const maxLeft = w - thumbW;
      if (maxLeft <= 0) return;
      const dx = e.clientX - this.hdrag.startX;
      this.scrollX = Math.max(0, Math.min(content - w, this.hdrag.startScroll + (dx / maxLeft) * (content - w)));
      this.invalidate();
    });
    window.addEventListener("mouseup", () => { this.hdrag = null; });
  }

  private hsKey = "";
  private updateHScroll() {
    const w = this.wrapEl.clientWidth, content = this.contentWidth();
    let key: string;
    if (content <= w + 1) key = "none";
    else {
      const sx = Math.max(0, Math.min(this.scrollX, content - w));
      if (sx !== this.scrollX) { this.scrollX = sx; this.invalidate(); }
      const thumbW = Math.max(30, (w / content) * w);
      key = `${thumbW.toFixed(1)}|${((sx / (content - w)) * (w - thumbW)).toFixed(1)}`;
    }
    if (key === this.hsKey) return; // avoid per-frame style writes
    this.hsKey = key;
    if (key === "none") { this.hbar.style.display = "none"; return; }
    const [tw, left] = key.split("|");
    this.hbar.style.display = "block";
    this.hthumb.style.width = tw + "px";
    this.hthumb.style.left = left + "px";
  }

  private recordOverlay: { sampleRate: number; tracks: Record<string, LivePeaksAt> } | null = null;
  setRecordOverlay(o: { sampleRate: number; tracks: Record<string, LivePeaksAt> } | null) { this.recordOverlay = o; }

  private drawRecordOverlay(g: CanvasRenderingContext2D, w: number) {
    const ov = this.recordOverlay;
    if (!ov) return;
    const lanes = this.laneTops();
    for (const lane of lanes) {
      const pk = ov.tracks[lane.id];
      if (!pk) continue;
      const startX = this.secToX(pk.startSec);
      const binPx = (pk.binSamples / ov.sampleRate) * this.pps;
      const n = pk.min.length;
      const lenPx = n * binPx;
      const top = lane.top + 2, ch = lane.baseH - 6;
      const mid = top + ch / 2, amp = ch / 2 - 3;
      // recording region box
      g.fillStyle = "rgba(255,77,77,0.12)";
      g.fillRect(startX, top, Math.max(2, lenPx), ch);
      g.strokeStyle = "#ff4d4d"; g.lineWidth = 1;
      g.strokeRect(startX + 0.5, top + 0.5, Math.max(2, lenPx) - 1, ch - 1);
      // waveform (bounded to visible columns; reduces bins-per-pixel when zoomed out)
      g.fillStyle = "#ff9a9a";
      const yLo = top + 1, yHi = top + ch - 1; // clamp drawing to the box
      if (binPx >= 1) {
        const i0 = Math.max(0, Math.floor((0 - startX) / binPx));
        const i1 = Math.min(n, Math.ceil((w - startX) / binPx));
        for (let i = i0; i < i1; i++) {
          const xx = startX + i * binPx;
          const yt = Math.max(yLo, mid - pk.max[i] * amp), yb = Math.min(yHi, mid - pk.min[i] * amp);
          if (yb > yt) g.fillRect(xx, yt, Math.max(1, binPx), Math.max(1, yb - yt));
        }
      } else {
        const px0 = Math.max(0, Math.floor(startX));
        const px1 = Math.min(w, Math.ceil(startX + lenPx));
        for (let px = px0; px < px1; px++) {
          const b0 = Math.max(0, Math.floor((px - startX) / binPx));
          const b1 = Math.min(n, Math.floor((px + 1 - startX) / binPx));
          let mn = 1, mx = -1;
          for (let b = b0; b < b1; b++) { if (pk.min[b] < mn) mn = pk.min[b]; if (pk.max[b] > mx) mx = pk.max[b]; }
          if (mx >= mn) { const yt = Math.max(yLo, mid - mx * amp), yb = Math.min(yHi, mid - mn * amp); if (yb > yt) g.fillRect(px, yt, 1, Math.max(1, yb - yt)); }
        }
      }
      // ● REC label
      g.fillStyle = "#ff4d4d"; g.font = "bold 10px Segoe UI";
      g.fillText("● REC", startX + 4, top + 11);
    }
  }

  draw() {
    this.updateHScroll();
    // Nothing changed since the last frame (idle, or a paused transport)? Skip the
    // full-canvas blit — this runs every animation frame.
    const m = this.marquee;
    const key = `${Math.round(this.secToX(this.engine.position()) * 2)}|${m ? `${m.x0},${m.y0},${m.x1},${m.y1}` : ""}`;
    if (!this.dirty && !this.recordOverlay && key === this.lastFrame) return;
    this.lastFrame = key;
    if (this.dirty) this.rebuildCache();
    const w = this.wrapEl.clientWidth, h = this.wrapEl.clientHeight;
    const g = this.c2d;
    g.clearRect(0, 0, w, h);
    g.drawImage(this.cache, 0, 0, this.cache.width, this.cache.height, 0, 0, w, h);
    this.drawRecordOverlay(g, w);
    if (this.marquee) {
      const m = this.marquee;
      const rx = Math.min(m.x0, m.x1), ry = Math.min(m.y0, m.y1);
      const rw = Math.abs(m.x1 - m.x0), rh = Math.abs(m.y1 - m.y0);
      g.fillStyle = "rgba(91,140,255,0.18)"; g.fillRect(rx, ry, rw, rh);
      g.strokeStyle = "#5b8cff"; g.lineWidth = 1; g.strokeRect(rx + 0.5, ry + 0.5, rw, rh);
    }
    // playhead
    const px = this.secToX(this.engine.position());
    if (px >= 0 && px <= w) {
      g.strokeStyle = "#ff4d4d"; g.lineWidth = 1.5;
      g.beginPath(); g.moveTo(px, 0); g.lineTo(px, h); g.stroke();
      g.fillStyle = "#ff4d4d"; g.beginPath();
      g.moveTo(px - 5, 0); g.lineTo(px + 5, 0); g.lineTo(px, 7); g.fill();
    }
  }

  // ---- interaction -----------------------------------------------------
  private bind() {
    this.wrapEl.addEventListener("wheel", (e) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const before = this.xToSec(e.offsetX);
        this.pps = Math.max(16, Math.min(400, this.pps * (e.deltaY < 0 ? 1.15 : 0.87)));
        this.scrollX = before * this.pps - e.offsetX;
      } else if (e.shiftKey) {
        this.scrollX = Math.max(0, this.scrollX + e.deltaY);
      } else {
        this.scrollX = Math.max(0, this.scrollX + e.deltaX);
        // Drive the native track-list scrollbar; its scroll handler updates scrollY.
        if (this.headsInner) this.headsInner.scrollTop += e.deltaY;
        else this.scrollY = Math.max(0, this.scrollY + e.deltaY);
      }
      this.invalidate();
    }, { passive: false });

    this.canvas.addEventListener("mousemove", (e) => {
      if (this.drag || this.pan) return;
      const rulerTip = e.offsetY < RULER_H ? "Click/drag: move the playhead · Shift-drag: set a loop region · Right-click: loop options" : "";
      if (this.canvas.title !== rulerTip) this.canvas.title = rulerTip;
      const hit = this.hitAt(e.offsetX, e.offsetY);
      this.canvas.style.cursor = e.offsetY < RULER_H ? "ew-resize"
        : hit?.zone === "left" || hit?.zone === "right" ? "ew-resize"
        : hit?.zone === "gain" ? "ns-resize"
        : hit?.zone === "take" ? "pointer"
        : hit ? "grab" : "grab"; // empty area = grab to pan
    });

    this.canvas.addEventListener("mousedown", (e) => {
      if (e.button !== 0) return;
      const x = e.offsetX, y = e.offsetY;
      if (y < RULER_H) {
        if (e.shiftKey) { // Shift-drag on the ruler = set a loop region
          const t0 = this.snapTime(Math.max(0, this.xToSec(x)), e.altKey);
          this.loopDrag = { s0: t0, s1: t0 }; this.invalidate(); return;
        }
        this.scrubbing = true; this.engine.seek(Math.max(0, this.xToSec(x))); return;
      }
      const hit = this.hitAt(x, y);
      if (hit?.zone === "take") { this.store.setActiveTake(hit.clipId); return; }
      if (hit) {
        const additive = e.shiftKey || e.ctrlKey || e.metaKey;
        if (additive) this.store.toggleClipSelection(hit.clipId);
        else if (!this.store.isClipSelected(hit.clipId)) this.store.select(hit.trackId, hit.clipId);
        else { this.store.selectedClipId = hit.clipId; this.store.selectedTrackId = hit.trackId; }
        // Only start a drag if the grabbed clip is (still) selected.
        if (this.store.isClipSelected(hit.clipId)) {
          const clip = this.store.findClip(hit.clipId)!.clip;
          const lane = this.laneTops().find((l) => l.id === hit.trackId)!;
          const starts = this.store.selectedClipList().map(({ clip }) => ({ id: clip.id, startSec: clip.start }));
          this.drag = { clipId: hit.clipId, mode: hit.zone as any, startSec: clip.start, grabX: x, startGain: clip.gain ?? 1, grabY: y, laneH: lane.baseH - 6, starts, undone: false };
        }
      } else {
        // Empty area: Shift-drag = marquee select; plain drag = pan; click = seek.
        if (e.shiftKey) {
          this.marquee = { x0: x, y0: y, x1: x, y1: y };
        } else {
          const lane = this.laneAt(y);
          this.pan = { startX: x, startScrollX: this.scrollX, startY: y, startTop: this.headsInner?.scrollTop ?? 0, moved: false, laneId: lane, seekX: x };
        }
      }
    });

    this.canvas.addEventListener("contextmenu", (e) => {
      e.preventDefault();
      if (e.offsetY < RULER_H) { // ruler → loop options
        const p = this.store.project, hasRegion = p.loopEnd > p.loopStart;
        openContextMenu(e.clientX, e.clientY, [
          { label: p.loopEnabled ? "Turn loop off" : "Turn loop on", onClick: () => { p.loopEnabled = !p.loopEnabled; this.store.emitChange("transport"); this.engine.refreshLoop(); } },
          { label: "Loop whole song (clear region)", disabled: !hasRegion, onClick: () => { this.store.setLoopRegion(0, 0); this.engine.refreshLoop(); } },
          { label: "Tip: Shift-drag the ruler to set a loop region", disabled: true, onClick: () => {} },
        ]);
        return;
      }
      const hit = this.hitAt(e.offsetX, e.offsetY);
      if (!hit) return;
      // If the clicked clip isn't part of the selection, select just it.
      if (!this.store.isClipSelected(hit.clipId)) this.store.select(hit.trackId, hit.clipId);
      const track = this.store.track(hit.trackId)!;
      const inGroup = !!this.store.findClip(hit.clipId)?.clip.takeGroupId;
      const n = this.store.selectedClipIds.size;
      const sfx = n > 1 ? ` (${n})` : "";
      openContextMenu(e.clientX, e.clientY, [
        { label: "Clip settings…" + sfx, onClick: () => this.cb.editClip?.(hit.clipId) },
        { label: "Split at playhead" + sfx, onClick: () => this.store.splitClipsAt(this.engine.position()) },
        { label: "Duplicate" + sfx, onClick: () => this.store.duplicateClips() },
        ...(hit.zone === "take" ? [{ label: "Promote take", onClick: () => this.store.setActiveTake(hit.clipId) }] : []),
        ...(inGroup ? [{ label: track.compExpanded ? "Collapse takes" : "Expand takes", onClick: () => this.store.toggleCompExpand(hit.trackId) }] : []),
        { label: "Delete" + sfx, onClick: () => this.store.removeClips() },
      ]);
    });

    // Double-click a clip to open its settings.
    this.canvas.addEventListener("dblclick", (e) => {
      if (e.offsetY < RULER_H) return;
      const hit = this.hitAt(e.offsetX, e.offsetY);
      if (hit) {
        if (!this.store.isClipSelected(hit.clipId)) this.store.select(hit.trackId, hit.clipId);
        else this.store.selectedClipId = hit.clipId;
        this.cb.editClip?.(hit.clipId);
      }
    });

    window.addEventListener("mousemove", (e) => {
      const rect = this.canvas.getBoundingClientRect();
      const x = e.clientX - rect.left, y = e.clientY - rect.top;
      if (this.scrubbing) { this.engine.seek(Math.max(0, this.xToSec(x))); return; }
      if (this.loopDrag) { this.loopDrag.s1 = this.snapTime(Math.max(0, this.xToSec(x)), e.altKey); this.invalidate(); return; }
      if (this.marquee) { this.marquee.x1 = x; this.marquee.y1 = y; return; }
      if (this.pan) {
        const dx = x - this.pan.startX, dy = y - this.pan.startY;
        if (Math.abs(dx) > 3 || Math.abs(dy) > 3) this.pan.moved = true;
        this.scrollX = Math.max(0, this.pan.startScrollX - dx);
        if (this.headsInner) this.headsInner.scrollTop = this.pan.startTop - dy;
        this.invalidate();
        return;
      }
      if (!this.drag) return;
      this.dragY = y;
      if (!this.drag.undone) { this.store.pushUndo(); this.drag.undone = true; } // lazy: only on real movement
      if (this.drag.mode === "move") {
        // Snap the grabbed clip, then shift the whole selection by the same delta.
        const snapped = this.snapTime(Math.max(0, this.drag.startSec + (x - this.drag.grabX) / this.pps), e.altKey);
        const delta = snapped - this.drag.startSec;
        for (const s of this.drag.starts) { const f = this.store.findClip(s.id); if (f) f.clip.start = Math.max(0, s.startSec + delta); }
        // cross-track move only when a single clip is selected
        if (this.drag.starts.length === 1) {
          const lane = this.laneAt(y);
          const cur = this.store.findClip(this.drag.clipId)?.track.id;
          if (lane && cur && lane !== cur) this.store.moveClipToTrack(this.drag.clipId, lane);
        }
        this.store.emitChange("tracks");
      } else if (this.drag.mode === "gain") {
        // drag up = louder; full lane height spans gain 0..2 — applied to all selected
        const g = Math.max(0, Math.min(2, this.drag.startGain + ((this.drag.grabY - y) / this.drag.laneH) * 2));
        this.store.updateSelectedClips({ gain: g }, false);
      } else {
        this.store.trimClip(this.drag.clipId, this.drag.mode, this.snapTime(this.xToSec(x), e.altKey));
      }
    });
    window.addEventListener("mouseup", () => {
      this.scrubbing = false;
      if (this.loopDrag) {
        const { s0, s1 } = this.loopDrag;
        this.loopDrag = null;
        if (Math.abs(s1 - s0) > 0.05) {
          this.store.setLoopRegion(s0, s1);
          this.store.project.loopEnabled = true; // setting a region turns looping on
          this.store.emitChange("transport");
          this.engine.refreshLoop();
        }
        this.invalidate();
      }
      if (this.marquee) { this.commitMarquee(); this.marquee = null; }
      if (this.drag?.mode === "move") {
        // Dropped in empty space below the last lane → spill into a new track.
        const lanes = this.laneTops();
        const bottom = lanes.length ? lanes[lanes.length - 1].top + lanes[lanes.length - 1].h : RULER_H;
        if (this.dragY > bottom) {
          const nt = newTrack(this.store.project.tracks.length);
          this.store.addTrack(nt);
          this.store.moveClipToTrack(this.drag.clipId, nt.id);
        }
      }
      this.drag = null;
      if (this.pan) {
        if (!this.pan.moved) { // treat as a click: seek + select the lane
          if (this.pan.laneId) this.store.select(this.pan.laneId, null);
          this.engine.seek(Math.max(0, this.xToSec(this.pan.seekX)));
        }
        this.pan = null;
      }
    });
  }

  private commitMarquee() {
    const m = this.marquee!;
    const rx0 = Math.min(m.x0, m.x1), rx1 = Math.max(m.x0, m.x1);
    const ry0 = Math.min(m.y0, m.y1), ry1 = Math.max(m.y0, m.y1);
    const ids: string[] = [];
    for (const lane of this.laneTops()) {
      const track = this.store.track(lane.id)!;
      const top = lane.top + 2, bot = lane.top + lane.baseH;
      if (bot < ry0 || top > ry1) continue; // lane outside marquee vertically
      for (const clip of track.clips) {
        if (clip.takeActive === false) continue;
        const cx = this.secToX(clip.start), cw = clip.duration * this.pps;
        if (cx + cw >= rx0 && cx <= rx1) ids.push(clip.id); // horizontal overlap
      }
    }
    this.store.setClipSelection(ids);
  }

  private laneAt(y: number): string | null {
    for (const lane of this.laneTops()) if (y >= lane.top && y < lane.top + lane.h) return lane.id;
    return null;
  }
  private hitAt(x: number, y: number): { trackId: string; clipId: string; zone: "move" | "left" | "right" | "take" | "gain" } | null {
    for (const lane of this.laneTops()) {
      if (y < lane.top || y >= lane.top + lane.h) continue;
      const track = this.store.track(lane.id)!;
      const inBase = y < lane.top + lane.baseH;
      if (inBase) {
        for (const clip of track.clips) {
          if (clip.takeActive === false) continue;
          const cx = this.secToX(clip.start), cw = clip.duration * this.pps;
          if (x >= cx && x <= cx + cw) {
            if (x <= cx + this.EDGE) return { trackId: lane.id, clipId: clip.id, zone: "left" };
            if (x >= cx + cw - this.EDGE) return { trackId: lane.id, clipId: clip.id, zone: "right" };
            // near the dashed gain line → gain drag
            const gy = this.gainLineY(lane.top + 2, lane.baseH - 6, clip.gain ?? 1);
            if (Math.abs(y - gy) <= 5) return { trackId: lane.id, clipId: clip.id, zone: "gain" };
            return { trackId: lane.id, clipId: clip.id, zone: "move" };
          }
        }
      } else {
        // drawer take alternates
        const row = Math.floor((y - lane.top - lane.baseH) / TAKE_ROW_HEIGHT);
        for (const clip of track.clips) {
          if (clip.takeActive !== false || !clip.takeGroupId) continue;
          if (this.takeRow(track, clip) !== row) continue;
          const cx = this.secToX(clip.start), cw = clip.duration * this.pps;
          if (x >= cx && x <= cx + cw) return { trackId: lane.id, clipId: clip.id, zone: "take" };
        }
      }
    }
    return null;
  }

  scrollToTime(sec: number) {
    const x = this.secToX(sec);
    const w = this.wrapEl.clientWidth;
    if (x < 0 || x > w - 60) { this.scrollX = Math.max(0, sec * this.pps - w * 0.3); this.invalidate(); }
  }

  zoomLabel() { return fmtBars(this.xToSec(this.wrapEl.clientWidth), this.store.project.tempo, this.store.project.beatsPerBar); }
}
